import React, { useCallback, useMemo, useState } from 'react'
import { CheckCircle2, ExternalLink, Loader2, Plus } from 'lucide-react'
import { Link } from 'react-router-dom'
import JSBI from 'jsbi'

import { nutrimaticLabels } from '@/nutrimatic'
import { useTranslation } from '@/features/locale/LocaleProvider'
import { cn } from '@/lib/utils'
import { Button } from '@/ui/button'
import { WalletActionButton } from '@/ui/wallet-action-button'
import { WalletActionWaitPanel } from '@/ui/wallet-action-wait-panel'
import { useWalletActionButton } from '../../../../hooks/useWalletActionButton'
import { LiquidityMobileTokenPickerSheet } from '@/mobile/token-picker/LiquidityMobileTokenPickerSheet'
import type { LiquidityTokenSearchState } from '@/features/liquidity/useLiquidityTokenSearch'
import { MintField as Field } from '../../../../domain/liquidity'
import type { AddLiquidityControllerResult } from '../../../../features/liquidity/useAddLiquidityController'
import { ApprovalState } from '../../../../hooks/useApproveCallback'
import { useAppWeb3 } from '../../../../hooks'
import { ChainId } from '../../../../sdk'
import { getScanLink } from '../../../../utils'
import { fractionExactAmount, resolveActiveBalanceFraction } from '../../../../utils/balanceFractionPresets'
import { NUTRIMATIC_BALANCE_FRACTIONS } from '../../../swap/SwapAmountField'
import { MobileSwapScreenHeader } from '../../../swap/nutrimatic/mobile/SwapNutrimaticMobileSimpleViews'
import { NutrimaticMobileNotice } from '../../../swap/nutrimatic/mobile/NutrimaticMobileNotice'
import { applyAmountKeypadKey, MobileAmountKeypad } from '../../../swap/nutrimatic/mobile/MobileAmountKeypad'
import {
  MobileAddLiquidityQuotePreview,
  MobileAddLiquidityRateHero,
  MobileAddLiquidityReviewDetails,
} from './MobileAddLiquidityQuoteBlocks'
import { NutrimaticMobileCurrencyLeg } from './NutrimaticMobileCurrencyLeg'
import { useAddLiquidityMobileNotice } from './useLiquidityMobileNotice'
import { useLiquidityAddNutrimaticFlow } from '../useLiquidityAddNutrimaticFlow'
import {
  isLiquidityWalletWaitActive,
  LiquidityNutrimaticMobileReviewWait,
} from './LiquidityNutrimaticMobileReviewWait'
import { LiquidityNutrimaticMobileOutcomeView } from './LiquidityNutrimaticMobileOutcomeView'
import {
  liquidityNutrimaticTabletHeaderProps,
  useLiquidityHubTabletBack,
} from '../useLiquidityHubTabletBack'

type ActiveField = Field.CURRENCY_A | Field.CURRENCY_B
type NutrimaticVariant = 'mobile' | 'desktop-tablet'

export function LiquidityNutrimaticMobileAddReviewView({
  controller,
  variant = 'mobile',
}: {
  controller: AddLiquidityControllerResult
  variant?: NutrimaticVariant
}) {
  const backToPositions = useLiquidityHubTabletBack()
  const {
    attemptingTxn,
    cancelLiquidityWalletWait,
    confirmAdd,
    currencies,
    handleReviewDismiss,
    noLiquidity,
    parsedAmounts,
    walletProgressPhase,
  } = controller

  const [submitting, setSubmitting] = useState(false)
  const isProcessing = isLiquidityWalletWaitActive({
    attemptingTxn,
    walletProgressPhase,
  })

  const onConfirm = () => {
    setSubmitting(true)
    void Promise.resolve(confirmAdd()).finally(() => setSubmitting(false))
  }

  return (
    <div className="nm-mobile-simple__review-stage flex min-h-0 flex-1 flex-col overflow-hidden">
      <MobileSwapScreenHeader
        title={nutrimaticLabels.mobileSimple.reviewScreenTitle}
        {...liquidityNutrimaticTabletHeaderProps(variant, backToPositions, {
          onBack: handleReviewDismiss,
          backLabel: nutrimaticLabels.cancel,
          hideBack: isProcessing,
        })}
      />

      <div className="nm-mobile-simple__body nm-mobile-simple__body--scroll" data-mobile-scroll-container>
        <p className="nm-mobile-simple__review-kicker">
          {noLiquidity ? nutrimaticLabels.mobileSimple.firstPoolBanner : nutrimaticLabels.mobileSimple.reviewAddLiquidity}
        </p>
        <p className="nm-mobile-simple__review-hero">
          {parsedAmounts[Field.CURRENCY_A]?.toSignificant(6)} {currencies[Field.CURRENCY_A]?.symbol} +{' '}
          {parsedAmounts[Field.CURRENCY_B]?.toSignificant(6)} {currencies[Field.CURRENCY_B]?.symbol}
        </p>

        <LiquidityNutrimaticMobileReviewWait
          attemptingTxn={attemptingTxn}
          walletProgressPhase={walletProgressPhase}
          onCancelWallet={cancelLiquidityWalletWait}
          reviewDetails={<MobileAddLiquidityReviewDetails controller={controller} />}
        />
      </div>

      {!isProcessing ? (
        <div className="nm-mobile-simple__footer">
          <Button
            type="button"
            className="nm-mobile-simple__cta gap-2"
            disabled={submitting}
            onClick={onConfirm}
          >
            {submitting ? <Loader2 className="size-5 animate-spin" aria-hidden /> : null}
            {submitting
              ? nutrimaticLabels.transactionSigning
              : noLiquidity
                ? nutrimaticLabels.mobileSimple.createPoolSupply
                : nutrimaticLabels.mobileSimple.confirmSupply}
          </Button>
        </div>
      ) : null}
    </div>
  )
}

export function LiquidityNutrimaticMobileAddCancelledView({
  controller,
}: {
  controller: AddLiquidityControllerResult
}) {
  const { dismissCancelledLiquidity, walletErrorMessage } = controller

  return (
    <LiquidityNutrimaticMobileOutcomeView
      kind="cancelled"
      operation="add"
      detail={walletErrorMessage}
      onBackToForm={dismissCancelledLiquidity}
    />
  )
}

export function LiquidityNutrimaticMobileAddErrorView({
  controller,
}: {
  controller: AddLiquidityControllerResult
}) {
  const { dismissCancelledLiquidity, retryLiquidityAfterError, walletErrorMessage } = controller

  return (
    <LiquidityNutrimaticMobileOutcomeView
      kind="error"
      operation="add"
      detail={walletErrorMessage}
      onBackToForm={dismissCancelledLiquidity}
      onRetry={retryLiquidityAfterError}
    />
  )
}

export function LiquidityNutrimaticMobileAddSuccessView({
  controller,
}: {
  controller: AddLiquidityControllerResult
}) {
  const { chainId } = useAppWeb3()
  const { currencies, handleDismissConfirmation, txHash } = controller
  const symbolA = currencies[Field.CURRENCY_A]?.symbol ?? ''
  const symbolB = currencies[Field.CURRENCY_B]?.symbol ?? ''
  const explorerHref = chainId && txHash ? getScanLink(chainId, txHash, 'transaction') : undefined

  return (
    <>
      <div className="nm-mobile-simple__success">
        <span className="nm-mobile-simple__hero-brand nm-mobile-simple__success-brand">
          {nutrimaticLabels.mobileSimple.productBrand}
        </span>
        <div className="nm-mobile-simple__success-icon">
          <CheckCircle2 className="size-9" aria-hidden />
        </div>
        <h2 className="nm-mobile-simple__success-title">
          {nutrimaticLabels.mobileSimple.addSuccessTitle(symbolA, symbolB)}
        </h2>
        {txHash ? <p className="nm-mobile-simple__hash-caption mt-4">{txHash}</p> : null}
        <div className="nm-mobile-simple__success-links">
          {explorerHref ? (
            <a
              href={explorerHref}
              target="_blank"
              rel="noopener noreferrer"
              className="nm-mobile-simple__success-link"
            >
              <ExternalLink className="size-4" aria-hidden />
              {nutrimaticLabels.mobileSimple.viewExplorer}
            </a>
          ) : null}
          <Link to="/portfolio/movements" className="nm-mobile-simple__success-link">
            <ExternalLink className="size-4" aria-hidden />
            {nutrimaticLabels.mobileSimple.viewActivity}
          </Link>
        </div>
      </div>
      <div className="nm-mobile-simple__footer nm-mobile-simple__footer--stacked">
        <Button type="button" className="nm-mobile-simple__cta" onClick={handleDismissConfirmation}>
          {nutrimaticLabels.mobileSimple.newLiquidityAction}
        </Button>
      </div>
    </>
  )
}

export function LiquidityNutrimaticMobileAddFormView({
  controller,
  tokenSearch,
  variant = 'mobile',
}: {
  controller: AddLiquidityControllerResult
  tokenSearch: LiquidityTokenSearchState
  variant?: NutrimaticVariant
}) {
  const isDesktopTablet = variant === 'desktop-tablet'
  const backToPositions = useLiquidityHubTabletBack()
  const { account } = useAppWeb3()
  const {
    approvalA,
    approvalB,
    approveACallback,
    approveBCallback,
    currencies,
    error,
    formattedAmounts,
    handleSupplyAction,
    isValid,
    maxAmounts,
    onFieldAInput,
    onFieldBInput,
    openConnectModal,
    parsedAmounts,
  } = controller

  const notice = useAddLiquidityMobileNotice(controller)
  const [activeField, setActiveField] = useState<ActiveField>(Field.CURRENCY_A)

  const activeMax = activeField === Field.CURRENCY_A ? maxAmounts[Field.CURRENCY_A] : maxAmounts[Field.CURRENCY_B]
  const showBalancePresets = Boolean(activeMax)
  const balancePresetsDisabled = Boolean(
    !account || !activeMax || !activeMax.greaterThan(JSBI.BigInt(0)),
  )

  const needsApproval =
    isValid &&
    (approvalA === ApprovalState.NOT_APPROVED ||
      approvalA === ApprovalState.PENDING ||
      approvalB === ApprovalState.NOT_APPROVED ||
      approvalB === ApprovalState.PENDING)

  const bothApproved = approvalA === ApprovalState.APPROVED && approvalB === ApprovalState.APPROVED
  const hasAmounts = Boolean(parsedAmounts[Field.CURRENCY_A] && parsedAmounts[Field.CURRENCY_B])

  const approvePendingOnChain =
    approvalA === ApprovalState.PENDING || approvalB === ApprovalState.PENDING

  const approveIdleLabel = useMemo(() => {
    if (approvalA !== ApprovalState.APPROVED) {
      return nutrimaticLabels.mobileSimple.approveToken(currencies[Field.CURRENCY_A]?.symbol ?? '')
    }
    return nutrimaticLabels.mobileSimple.approveToken(currencies[Field.CURRENCY_B]?.symbol ?? '')
  }, [approvalA, approvalB, currencies])

  const {
    label: approvePrimaryLabel,
    phase: approvePrimaryPhase,
    isBusy: approvePrimaryBusy,
    canCancel: canCancelApprovePrimary,
    disabled: approvePrimaryDisabled,
    run: runApprovePrimary,
    cancel: cancelApprovePrimary,
  } = useWalletActionButton({
    onAction: async () => {
      if (approvalA !== ApprovalState.APPROVED) await approveACallback()
      else await approveBCallback()
    },
    pendingOnChain: approvePendingOnChain,
    labels: useMemo(() => ({ idle: approveIdleLabel }), [approveIdleLabel]),
    disabledWhenIdle: !needsApproval || !isValid,
  })

  const primaryLabel = !account
    ? nutrimaticLabels.mobileSimple.connectWallet
    : needsApproval
      ? approvePrimaryLabel
      : nutrimaticLabels.mobileSimple.reviewAddLiquidity

  const primaryDisabled =
    !account
      ? false
      : needsApproval
        ? approvePrimaryDisabled
        : !isValid || !bothApproved || !hasAmounts

  const primaryLoading = Boolean(account && needsApproval && approvePrimaryBusy)
  const showApproveWaitPanel = Boolean(account && needsApproval && approvePrimaryPhase !== 'idle')

  const onPrimary = () => {
    if (!account) {
      openConnectModal()
      return
    }
    if (needsApproval) {
      runApprovePrimary()
      return
    }
    handleSupplyAction()
  }

  const applyKeypadToField = useCallback(
    (key: string) => {
      const current =
        activeField === Field.CURRENCY_A
          ? formattedAmounts[Field.CURRENCY_A]
          : formattedAmounts[Field.CURRENCY_B]
      const maxDecimals =
        activeField === Field.CURRENCY_A
          ? currencies[Field.CURRENCY_A]?.decimals
          : currencies[Field.CURRENCY_B]?.decimals
      const next = applyAmountKeypadKey(current, key, { maxDecimals })
      if (activeField === Field.CURRENCY_A) onFieldAInput(next)
      else onFieldBInput(next)
    },
    [activeField, currencies, formattedAmounts, onFieldAInput, onFieldBInput],
  )

  const applyBalanceFraction = useCallback(
    (fraction: number) => {
      const max = activeField === Field.CURRENCY_A ? maxAmounts[Field.CURRENCY_A] : maxAmounts[Field.CURRENCY_B]
      if (!max) return
      const exact = fractionExactAmount(max, fraction)
      if (activeField === Field.CURRENCY_A) onFieldAInput(exact)
      else onFieldBInput(exact)
    },
    [activeField, maxAmounts, onFieldAInput, onFieldBInput],
  )

  const activeBalanceFraction = useMemo(() => {
    const max = activeField === Field.CURRENCY_A ? maxAmounts[Field.CURRENCY_A] : maxAmounts[Field.CURRENCY_B]
    const parsed =
      activeField === Field.CURRENCY_A ? parsedAmounts[Field.CURRENCY_A] : parsedAmounts[Field.CURRENCY_B]
    return resolveActiveBalanceFraction(parsed, max, NUTRIMATIC_BALANCE_FRACTIONS)
  }, [activeField, maxAmounts, parsedAmounts])

  return (
    <div className="nm-mobile-simple__convert-stage flex min-h-0 flex-1 flex-col overflow-hidden">
      <MobileSwapScreenHeader
        title={nutrimaticLabels.mobileSimple.addLiquidityTitle}
        rateHero={isDesktopTablet ? undefined : <MobileAddLiquidityRateHero controller={controller} />}
        {...liquidityNutrimaticTabletHeaderProps(variant, backToPositions)}
      />

      <div className="nm-mobile-simple__body nm-mobile-simple__body--trade" data-mobile-scroll-container>
        {notice ? <NutrimaticMobileNotice {...notice} /> : null}

        <div className="nm-mobile-simple__trade-stack">
          <NutrimaticMobileCurrencyLeg
            value={formattedAmounts[Field.CURRENCY_A]}
            currency={currencies[Field.CURRENCY_A]}
            onOpenTokenPicker={() => tokenSearch.openForField('A')}
            onFocusAmount={() => setActiveField(Field.CURRENCY_A)}
            onAmountChange={onFieldAInput}
            active={activeField === Field.CURRENCY_A}
            position="from"
            useNativeInput={isDesktopTablet}
          />

          <NutrimaticMobileCurrencyLeg
            value={formattedAmounts[Field.CURRENCY_B]}
            currency={currencies[Field.CURRENCY_B]}
            onOpenTokenPicker={() => tokenSearch.openForField('B')}
            onFocusAmount={() => setActiveField(Field.CURRENCY_B)}
            onAmountChange={onFieldBInput}
            active={activeField === Field.CURRENCY_B}
            position="to"
            useNativeInput={isDesktopTablet}
          />

          <div className="nm-mobile-simple__swap-bridge" aria-hidden>
            <span className="nm-mobile-simple__swap-bridge-icon">
              <Plus className="size-4" />
            </span>
          </div>
        </div>

        <MobileAddLiquidityQuotePreview controller={controller} />
      </div>

      <div className={cn('nm-mobile-simple__input-dock', isDesktopTablet && 'nm-mobile-simple__input-dock--desktop')}>
        <div className="nm-mobile-simple__footer">
          {showApproveWaitPanel ? (
            <WalletActionWaitPanel
              phase={approvePrimaryPhase === 'awaiting-confirmation' ? 'awaiting-confirmation' : 'awaiting-wallet'}
              onCancel={canCancelApprovePrimary ? cancelApprovePrimary : undefined}
            />
          ) : (
            <WalletActionButton
              type="button"
              className={cn('nm-mobile-simple__cta', !primaryDisabled && error && hasAmounts && 'bg-destructive')}
              disabled={primaryDisabled}
              loading={primaryLoading}
              onClick={onPrimary}
            >
              {account && error && !needsApproval && hasAmounts ? error : primaryLabel}
            </WalletActionButton>
          )}
        </div>

        {showBalancePresets ? (
          <div className="nm-mobile-simple__presets">
            {NUTRIMATIC_BALANCE_FRACTIONS.map((fraction) => {
              const isActive = activeBalanceFraction === fraction
              const label =
                fraction === 1 ? nutrimaticLabels.mobileSimple.maxPreset : `${Math.round(fraction * 100)}%`
              return (
                <button
                  key={fraction}
                  type="button"
                  className={cn('nm-mobile-simple__preset', isActive && 'nm-mobile-simple__preset--active')}
                  disabled={balancePresetsDisabled}
                  onClick={() => applyBalanceFraction(fraction)}
                >
                  {label}
                </button>
              )
            })}
          </div>
        ) : null}

        {!isDesktopTablet ? <MobileAmountKeypad onKeyPress={applyKeypadToField} /> : null}
      </div>
    </div>
  )
}

/** Flujo móvil completo (form → review → success) con picker fullscreen. */
export function LiquidityNutrimaticMobileAddView({ controller }: { controller: AddLiquidityControllerResult }) {
  const { chainId } = useAppWeb3()
  const t = useTranslation()
  const { screen, tokenSearch } = useLiquidityAddNutrimaticFlow(controller)

  const onTokenPick = useCallback(
    (field: 'A' | 'B', currency: Parameters<typeof controller.handleCurrencyASelect>[0]) => {
      if (field === 'A') controller.handleCurrencyASelect(currency)
      else controller.handleCurrencyBSelect(currency)
    },
    [controller],
  )

  if (screen === 'success') {
    return <LiquidityNutrimaticMobileAddSuccessView controller={controller} />
  }

  if (screen === 'cancelled') {
    return <LiquidityNutrimaticMobileAddCancelledView controller={controller} />
  }

  if (screen === 'error') {
    return <LiquidityNutrimaticMobileAddErrorView controller={controller} />
  }

  if (screen === 'review') {
    return <LiquidityNutrimaticMobileAddReviewView controller={controller} />
  }

  return (
    <>
      <LiquidityMobileTokenPickerSheet
        open={tokenSearch.open}
        onClose={tokenSearch.close}
        chainId={chainId ?? ChainId.INFINITE_CREATIVE}
        title={tokenSearch.activeField === 'A' ? t.common.selectTokenA : t.common.selectTokenB}
        excludeCurrency={
          tokenSearch.activeField === 'A'
            ? (controller.currencies[Field.CURRENCY_B] ?? undefined)
            : (controller.currencies[Field.CURRENCY_A] ?? undefined)
        }
        onSelect={(currency) => tokenSearch.selectCurrency(currency, onTokenPick)}
      />

      <LiquidityNutrimaticMobileAddFormView
        controller={controller}
        tokenSearch={tokenSearch}
        variant="mobile"
      />
    </>
  )
}
